// @ts-nocheck
/* Port of InstructionCard.js (143L). BYTE-IDENTICAL.
 * Persists collapsed state in localStorage under `rc_guide_collapsed`.
 */
import React, { useState } from 'react';
import { FiChevronDown, FiBookOpen } from 'react-icons/fi';

const GUIDE_KEY = 'rc_guide_collapsed';

const STEPS = [
  {
    title: 'Start a release',
    body: (
      <>
        Click <code>+ New release</code> (top-right or the floating ✦ dock).
        Set a unique <code>Version</code> like <code>2.4.0</code>, a punchy{' '}
        <code>Title</code>, the <code>Type</code> (Major / Minor / Patch /
        Hotfix / Security) and the <code>Release date</code> (use the{' '}
        <code>Today</code> button for a quick pick).
      </>
    ),
  },
  {
    title: 'Write for scanners',
    body: (
      <>
        Add a one-line <code>Summary</code> for the card and a{' '}
        <code>Quick summary</code> intro. People scan — keep it crisp.
      </>
    ),
  },
  {
    title: 'Add "What changed"',
    body: (
      <>
        Under <code>What changed</code>, add checklist items and tag each as{' '}
        <code>Added</code>, <code>Improved</code>, <code>Fixed</code> or{' '}
        <code>Removed</code>. These power the checklist AND the side-by-side{' '}
        <code>Compare</code> view.
      </>
    ),
  },
  {
    title: 'Tag & detail',
    body: (
      <>
        Pick <code>Tags</code> (Voice, CRM, API, Analytics, WhatsApp, UI…) so
        filters work, then write the full notes in the rich editor — images,
        tables, code blocks, videos and links are all supported.
      </>
    ),
  },
  {
    title: 'Publish or pin',
    body: (
      <>
        Toggle <code>Published</code> to make it live (leave off to keep a{' '}
        <code>Draft</code>). Toggle <code>Pinned</code> to feature it as the
        large hero card at the top.
      </>
    ),
  },
];

const InstructionCard = () => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(GUIDE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggle = () => {
    setCollapsed((prev: boolean) => {
      const next = !prev;
      try {
        localStorage.setItem(GUIDE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  return (
    <div
      className={`rc-guide rc-fade-up ${collapsed ? 'rc-guide--collapsed' : ''}`}
      role="note"
      aria-label="How to create a release"
    >
      <button
        type="button"
        className="rc-guide__toggle"
        onClick={toggle}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand guide' : 'Minimize guide'}
        title={collapsed ? 'Expand' : 'Minimize'}
      >
        <span className="rc-guide__chevron" aria-hidden="true">
          <FiChevronDown size={18} />
        </span>
      </button>

      <div
        className="rc-guide__head"
        onClick={toggle}
        style={{ cursor: 'pointer' }}
      >
        <span className="rc-guide__badge" aria-hidden="true">
          <FiBookOpen size={20} />
        </span>
        <div>
          <h5 className="rc-guide__title">How to publish a release</h5>
          <p className="rc-guide__sub">
            {collapsed
              ? 'Minimized — click to expand the step-by-step guide.'
              : 'A quick guide for the team — what to create and how.'}
          </p>
        </div>
      </div>

      {!collapsed && (
        <>
          <div className="rc-guide__steps">
            {STEPS.map((s: any, i: number) => (
              <div className="rc-guide__step" key={i}>
                <span className="rc-guide__step-n">{i + 1}</span>
                <h6>{s.title}</h6>
                <p>{s.body}</p>
              </div>
            ))}
          </div>

          <div className="rc-guide__foot">
            <span>
              💡 Tip: releases are never deleted — move them to Draft instead.
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default InstructionCard;
